<script lang="ts">
    import { apiClient } from "./apiClient";
    import type { Table } from "./apiClient";

    export let table: Table | null = null;
    export let isOpen = false;
    export let onClose: () => void = () => {};

    let loading = false;
    let error: string | null = null;
    let svgContent: string | null = null;
    let pngUrl: string | null = null;

    $: if (isOpen && table) {
        loadQRCodes();
    }

    async function loadQRCodes() {
        if (!table) return;

        loading = true;
        error = null;
        svgContent = null;
        pngUrl = null;

        try {
            // Load SVG version for preview
            const svgBlob = await apiClient.getTableQRCode(table.name, "svg");
            svgContent = await svgBlob.text();

            // Load PNG version for download
            const pngBlob = await apiClient.getTableQRCode(table.name, "png");
            pngUrl = URL.createObjectURL(pngBlob);
        } catch (err) {
            error =
                err instanceof Error ? err.message : "Failed to load QR codes";
        } finally {
            loading = false;
        }
    }

    async function downloadQRCode(format: "svg" | "png") {
        if (!table) return;

        try {
            const blob = await apiClient.getTableQRCode(table.name, format);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `table-${table.name}-qr.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            error =
                err instanceof Error
                    ? err.message
                    : `Failed to download ${format.toUpperCase()} file`;
        }
    }

    function handleBackdropClick(event: MouseEvent) {
        if (event.target === event.currentTarget) {
            closeModal();
        }
    }

    function closeModal() {
        if (pngUrl) {
            URL.revokeObjectURL(pngUrl);
            pngUrl = null;
        }
        svgContent = null;
        error = null;
        onClose();
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.key === "Escape") {
            closeModal();
        }
    }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
        class="modal-backdrop"
        on:click={handleBackdropClick}
        on:keydown={handleKeydown}
        role="dialog"
    >
        <div class="modal">
            <div class="modal-header">
                <h3>QR Code for Table {table?.name}</h3>
                <button class="close-btn" on:click={closeModal}>&times;</button>
            </div>

            <div class="modal-body">
                {#if loading}
                    <div class="loading">Loading QR codes...</div>
                {:else if error}
                    <div class="error">
                        <p>{error}</p>
                        <button class="btn btn-primary" on:click={loadQRCodes}>
                            Retry
                        </button>
                    </div>
                {:else if svgContent}
                    <div class="qr-preview">
                        <div class="qr-code">
                            {@html svgContent}
                        </div>
                        <p class="qr-info">
                            This QR code will direct customers to the drinks
                            menu with table "<strong>{table?.name}</strong>"
                            pre-selected.
                        </p>
                    </div>

                    <div class="download-buttons">
                        <button
                            class="btn btn-secondary"
                            on:click={() => downloadQRCode("svg")}
                        >
                            Download as SVG
                        </button>
                        <button
                            class="btn btn-secondary"
                            on:click={() => downloadQRCode("png")}
                        >
                            Download as PNG
                        </button>
                    </div>
                {/if}
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 8px;
        padding: 0;
        min-width: 400px;
        max-width: 90vw;
        max-height: 90vh;
        overflow: auto;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e1e5e9;
        background-color: #f8f9fa;
        border-radius: 8px 8px 0 0;
    }

    .modal-header h3 {
        margin: 0;
        font-size: 1.2rem;
        color: #495057;
    }

    .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        font-weight: bold;
        color: #6c757d;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition:
            background-color 0.2s,
            color 0.2s;
    }

    .close-btn:hover {
        background-color: #e9ecef;
        color: #495057;
    }

    .modal-body {
        padding: 1.5rem;
    }

    .loading {
        text-align: center;
        padding: 2rem;
        color: #6c757d;
    }

    .error {
        text-align: center;
        padding: 2rem;
        color: #dc3545;
    }

    .qr-preview {
        text-align: center;
        margin-bottom: 2rem;
    }

    .qr-code {
        display: inline-block;
        background: white;
        padding: 1rem;
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        margin-bottom: 1rem;
    }

    .qr-code :global(svg) {
        max-width: 200px;
        max-height: 200px;
        width: 100%;
        height: auto;
    }

    .qr-info {
        color: #6c757d;
        font-size: 0.9rem;
        margin: 0;
        line-height: 1.4;
    }

    .download-buttons {
        display: flex;
        gap: 1rem;
        justify-content: center;
        flex-wrap: wrap;
    }

    .btn {
        padding: 0.5rem 1rem;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-weight: 500;
        text-decoration: none;
        display: inline-block;
        transition: all 0.2s;
    }

    .btn-primary {
        background-color: #007bff;
        color: white;
    }

    .btn-primary:hover {
        background-color: #0056b3;
    }

    .btn-secondary {
        background-color: #6c757d;
        color: white;
    }

    .btn-secondary:hover {
        background-color: #545b62;
    }
</style>
